import ButtonLink from "@components/Molecules/ButtonLink";
import styled from "@emotion/styled";
import { TitledLink } from "@ext/navigation/NavigationLinks";
import { Fragment, ReactNode } from "react";
import Divider from "../Atoms/Divider";

interface RenderTitledLinks {
	links: TitledLink[];
	isCatalog?: boolean;
	isChildren?: boolean;
}

const RenderTitledLink = ({ link }: { link: TitledLink }): JSX.Element => {
	return (
		<a
			className="layout_link"
			data-qa="qa-clickable"
			href={link.url}
			rel="noreferrer"
			target={link.target ?? "_blank"}
		>
			<ButtonLink iconCode={link.icon} text={link.title} />
			{link.childrens && <RenderTitledLinks isChildren={true} links={link.childrens} />}
		</a>
	);
};

const RenderTitledLinks = ({ links, isCatalog, isChildren }: RenderTitledLinks): ReactNode => {
	if (isChildren) {
		return links.map((link, idx) => (
			<Fragment key={idx}>
				{idx != 0 ? "/" : ""}
				<RenderTitledLink link={link} />
			</Fragment>
		));
	}

	return links.map((link, key) =>
		!Object.keys(link).length ? (
			<Divider className="divider" key={key} />
		) : (
			<li data-qa={`${isCatalog ? "catalog" : "article"}-${link.icon}-button`} key={key} onClick={link.onClick}>
				<RenderTitledLink link={link} />
			</li>
		),
	);
};

const Links = (props: {
	articleLinks?: TitledLink[];
	catalogLinks?: TitledLink[];
	articleChildren?: JSX.Element;
	isArticleActionsVisible?: boolean;
	catalogChildren?: JSX.Element;
	isCatalogActionsVisible?: boolean;
	className?: string;
}) => {
	const {
		articleLinks = [],
		articleChildren,
		isArticleActionsVisible,
		catalogLinks = [],
		catalogChildren,
		isCatalogActionsVisible,
		className,
	} = props;
	return (
		<ul className={className}>
			{articleLinks?.length || isArticleActionsVisible ? <Divider className="mt-4 mb-4" /> : null}
			<ul className={className}>
				<RenderTitledLinks isCatalog={false} links={articleLinks} />
				{articleChildren}
			</ul>
			{catalogLinks?.length || isCatalogActionsVisible ? <Divider /> : null}
			<ul className={className}>
				<RenderTitledLinks isCatalog={true} links={catalogLinks} />
				{catalogChildren}
			</ul>
		</ul>
	);
};

export default styled(Links)`
	margin-left: -20px !important;

	.layout_link {
		display: inline-flex;
	}

	a:hover {
		color: var(--color-primary);
		text-decoration: none !important;
	}

	span a:first-of-type {
		margin-left: 0 !important;
	}

	span a:last-child {
		margin-right: 0 !important;
	}

	span a {
		margin-right: 0.3em;
		margin-left: 0.3em;
	}

	.icon {
		color: var(--color-primary);
	}

	> ul > li,
	.divider {
		line-height: 1.2em;
	}

	.fa-fw {
		margin-left: -1.25rem;
	}
`;
